import {
  fireEvent,
  hideChatbot,
  setMetadata,
  setOnMessage,
  setOpen,
  showChatbot,
  subscribeToEvent,
  trackEvent,
  unsubscribeFromEvent,
} from './api';
import {
  injectChatElement,
  injectScript,
  isChatElementMounted,
  removeChatbotScript,
  removeChatElement,
} from './dom';
import type {
  AppliedChatbotConfig,
  AppliedEventKey,
  AppliedMetadata,
  ChatbotLogger,
  ChatEventCallback,
  ChatMessageCallback,
} from './types';

export interface ChatbotInstanceOptions {
  config: AppliedChatbotConfig;
  logger?: ChatbotLogger | Console;
}

export class Chatbot {
  private config: AppliedChatbotConfig;
  private logger: ChatbotLogger | Console;

  constructor({ config, logger }: ChatbotInstanceOptions) {
    this.config = config;
    this.logger = logger ?? console;
  }

  injectScript() {
    injectScript(this.logger);
  }

  injectElement() {
    injectChatElement(this.config, this.logger);
  }

  setMetadata(metadata: AppliedMetadata) {
    setMetadata(metadata, this.logger);
  }

  updateMetadata(metadata: Partial<AppliedMetadata>) {
    this.setMetadata({ ...this.config.metadata, ...metadata });
  }

  show() {
    showChatbot(this.logger);
  }

  hide() {
    hideChatbot(this.logger);
  }

  setOpen(open: boolean) {
    setOpen(open, this.logger);
  }

  onMessage(callback: ChatMessageCallback) {
    setOnMessage(callback, this.logger);
  }

  subscribeToEvent<T>(event: AppliedEventKey, callback: ChatEventCallback<T>): number {
    return subscribeToEvent(event, callback, this.logger);
  }

  unsubscribeFromEvent(subscriptionId: number) {
    unsubscribeFromEvent(subscriptionId, this.logger);
  }

  fireEvent(event: string, payload?: unknown) {
    fireEvent(event, payload, this.logger);
  }

  trackEvent(event: string, value?: unknown, meta?: Record<string, unknown>) {
    trackEvent(event, value, meta, this.logger);
  }

  // Custom methods
  mount(config?: Partial<AppliedChatbotConfig>) {
    this.config = { ...this.config, ...config };
    this.injectScript();
    this.injectElement();
  }

  unmount() {
    removeChatElement(this.logger);
    removeChatbotScript(this.logger);
  }

  isMounted() {
    return isChatElementMounted();
  }
}
